import { useState } from "react";
import VisitorTypePageView from "./VisitorTypePageView";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";
import useCustomEffect from "../../../../hooks/useCustomEffect";
import { useFrontofficeContext } from "../../../../context/FrontofficeContext";
import useFlowPlayer, {
  FlowPlayer,
} from "../../../../components/flowPlayer/FlowPlayer";
import {
  getFlowDataFromVisitor,
  getVisitorFetchParams,
} from "../../../../components/flowPlayer/FlowPlayerHelpers";
import { useLanguageContext } from "../../../../context/LanguageContext";
import EVAApiHelper from "../../../../helpers/EVAApiHelper";

function VisitorTypePage() {
  const callEvaApi = useEvaApi();
  const frontofficeContext = useFrontofficeContext();
  const { translate } = useLanguageContext();

  const signIn = frontofficeContext.pageParams?.signIn ?? true;
  const simulated = frontofficeContext.pageParams?.simulated ?? false;
  const visitorType =
    frontofficeContext.pageParams?.visitorType ??
    frontofficeContext.pageParams?.visitor?.visitor_type;
  const flowId = visitorType?.[signIn ? "sign_in_flow" : "sign_out_flow"];

  const [visitors, setVisitors] = useState([]);
  const [visitor, setVisitor] = useState(
    frontofficeContext.pageParams?.visitor,
  );
  const [visitorData, setVisitorData] = useState({
    sign_in_terminal_id: frontofficeContext.terminal.id,
    sign_in_bc_terminal_id: localStorage.getItem("bc_terminal_id") || null,
    visitor_type_id: visitorType?.id,
    visitor_id: visitor?.id,
    ...getFlowDataFromVisitor(visitor),
  });
  const [submitted, setSubmitted] = useState(false);
  const flowPlayer = useFlowPlayer(
    new FlowPlayer()
      .setFlowId(flowId)
      .setVisitor(visitor)
      .setVisitorType(visitorType)
      .setVisitorState(visitorData, setVisitorData)
      .setSubmitted(submitted)
      .setSimulated(simulated)
      .setOnSubmit(onSubmit)
      .setOnBeforeNextStep(onBeforeNextStep)
      .setOnCancelPress(() => frontofficeContext.updatePage("start"))
      .setOnFlowEnd(() => frontofficeContext.updatePage("start")),
  );

  /**
   * UseEffect for initializing
   */
  useCustomEffect(() => {
    fetchVisitors();

    if (!flowPlayer.step) {
      flowPlayer.onNextStep();
    }
  });

  /**
   * This method will fetch the possible visitors for the visitor type
   */
  async function fetchVisitors() {
    if (visitor) {
      return;
    }

    await callEvaApi(
      new EvaApiCall("visitors")
        .setLoadingGroup("visitor_type_page_fetch")
        .setParams({
          ...getVisitorFetchParams(),
          scheduled_only: true,
          where: [
            {
              column: "visitor_type_id",
              values: [visitorType?.id],
            },
          ],
        })
        .setOnSuccess((response) => {
          setVisitors(response.data.data.records);
        }),
    );
  }

  /**
   * This method will search for a matching visitor
   * @returns {object}
   */
  findMatchingVisitor();
  function findMatchingVisitor() {
    if (visitor) return;

    const possibleVisitors = frontofficeContext.filterVisitorsBySearch(
      frontofficeContext.getVisitorIdentifierString(visitorData, visitorType),
      visitors,
      true,
    );

    if (possibleVisitors.length === 1) {
      const newVisitor = possibleVisitors[0];
      onVisitorChange(newVisitor);
    }
  }

  /**
   * This method will handle the visitor change event
   * @param {object} newVisitor
   */
  function onVisitorChange(newVisitor) {
    setVisitor(newVisitor);
    setVisitorData((prev) => ({
      ...prev,
      visitor_id: newVisitor.id,
      ...getFlowDataFromVisitor(newVisitor),
    }));
  }

  /**
   * This method will be called before the flow goes to the next step
   * It stops the sign in flow as soon as the visitor turns out to be signed in already
   * (signed_in_at is also what the backend checks when signing in).
   * This only applies before this flow signed the visitor in, so the steps after the sign in (like printing) can continue.
   * @returns {boolean} false to stop the flow
   */
  function onBeforeNextStep() {
    if (signIn && !submitted && visitor?.signed_in_at) {
      showAlreadySignedIn();
      return false;
    }

    return true;
  }

  /**
   * This method will show the message that the visitor is already signed in, with a button back to the start page
   */
  function showAlreadySignedIn() {
    frontofficeContext.updatePage("message", {
      title: translate(
        "eva.frontoffice.pages.visitor_type.already_signed_in.title",
      ),
      message: translate(
        "eva.frontoffice.pages.visitor_type.already_signed_in.message",
        [
          {
            key: "name",
            value: frontofficeContext.getVisitorIdentifierString(
              visitor,
              visitorType,
            ),
          },
        ],
      ),
    });
  }

  /**
   * This method will show the message that signing in or out failed, so the terminal never gets stuck on a failed request
   * @param {object} error
   */
  function showSubmitError(error) {
    frontofficeContext.updatePage("message", {
      title: translate(
        `eva.frontoffice.pages.visitor_type.errors.${signIn ? "sign_in_failed" : "sign_out_failed"}`,
      ),
      message: EVAApiHelper.getResponseError("message", error, true),
    });
  }

  /**
   * This method will handle the flow completion event
   */
  async function onSubmit(step = null) {
    step = step || flowPlayer.step;

    await callEvaApi(
      new EvaApiCall(
        `visitors/${signIn ? "sign_in" : "sign_out"}${visitor?.id ? `/${visitor?.id}` : ""}`,
      )
        .setLoadingGroup(step ? "flow_player_next_step" : "flow_player_fetch")
        .setParams(getVisitorFetchParams())
        .setMethod("POST")
        .setAlertError(false)
        .setAlertSuccess(false)
        .setData(visitorData)
        .setOnSuccess((response) => {
          if (!flowId) {
            frontofficeContext.updatePage("start");
            return;
          }

          const newVisitor = response.data.data;
          onVisitorChange(newVisitor);
          flowPlayer.onNextStep(step);
          setSubmitted(true);
        })
        .setOnError(showSubmitError),
    );
  }

  return (
    <VisitorTypePageView flowPlayer={flowPlayer} visitorType={visitorType} />
  );
}

export default VisitorTypePage;
